import React, { useState, useEffect, useRef } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useNavigate } from 'react-router-dom';
import emailService from '../../../services/emailService';

const VerificationAndQRStep = ({ formData, updateFormData, onPrevious, onSubmit, isSubmitting, submitError }) => {
  const navigate = useNavigate();
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);
  const [errors, setErrors] = useState({});
  const [businessDocPreview, setBusinessDocPreview] = useState(null);
  const [taxDocPreview, setTaxDocPreview] = useState(null);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [tempEmail, setTempEmail] = useState('');

  // Timer for code expiration
  useEffect(() => {
    let interval;
    if (codeSent && remainingTime > 0) {
      interval = setInterval(() => {
        const timeLeft = emailService?.getRemainingTime(formData?.contactEmail);
        setRemainingTime(timeLeft);
        
        if (timeLeft === 0) {
          setCanResend(true);
        }
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [codeSent, remainingTime, formData?.contactEmail]);

  const handleSendCode = async () => {
    if (!formData?.contactEmail) {
      setError('Por favor ingresa tu correo electrónico');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Usar el mismo método que funciona en user registration
      const result = await emailService?.sendVerificationEmail(formData?.contactEmail);

      if (result?.success) {
        setCodeSent(true);
        setRemainingTime(600); // 10 minutos (600 segundos)
        setCanResend(false);
        setSuccess('Código enviado exitosamente. Revisa la consola del navegador (F12 → Console) para ver tu código de 6 dígitos.');
        console.log('\n⚠️ IMPORTANTE: Busca el código de 6 dígitos en los mensajes de arriba ⬆️\n');
      } else {
        setError(result?.message || 'Error al enviar el código');
      }
    } catch (err) {
      console.error('Error al enviar código:', err);
      setError('Error al enviar el código. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setVerificationCode(['', '', '', '', '', '']);
    await handleSendCode();
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/?.test(value)) return;

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs?.current?.[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e?.key === 'Backspace' && !verificationCode?.[index] && index > 0) {
      inputRefs?.current?.[index - 1]?.focus();
    }
  };

  const handleChangeEmail = async () => {
    if (!tempEmail || !tempEmail?.includes('@')) {
      setErrors({ ...errors, emailChange: 'Correo electrónico inválido' });
      return;
    }
    updateFormData({ contactEmail: tempEmail });
    setIsEditingEmail(false);
    setCodeSent(false);
    setErrors({ ...errors, emailChange: null });
  };

  const handleCancelEmailChange = () => {
    setTempEmail(formData?.contactEmail || '');
    setIsEditingEmail(false);
    setErrors({ ...errors, emailChange: null });
  };

  const handleVerify = async () => {
    const code = verificationCode?.join('');
    
    if (code?.length !== 6) {
      setError('Por favor ingresa el código completo de 6 dígitos');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Usar el mismo método que funciona en user registration
      const result = await emailService?.verifyCode(formData?.contactEmail, code);
      
      if (result?.success) {
        setSuccess('¡Código verificado correctamente!');
        updateFormData({ emailVerified: true, verificationCode: code });
        
        // Pequeña pausa para mostrar el mensaje de éxito
        setTimeout(() => {
          // El componente padre manejará el submit
        }, 1000);
      } else {
        setError(result?.message || 'Código incorrecto');
        setVerificationCode(['', '', '', '', '', '']);
        if (inputRefs?.current?.[0]) {
          inputRefs?.current?.[0]?.focus();
        }
      }
    } catch (err) {
      console.error('Error al verificar código:', err);
      setError('Error al verificar el código. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const handleBusinessDocUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (file?.size > 10 * 1024 * 1024) {
        setErrors({ ...errors, businessDocument: 'El archivo no debe superar 10MB' });
        return;
      }

      if (!['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']?.includes(file?.type)) {
        setErrors({ ...errors, businessDocument: 'Solo se permiten archivos JPG, PNG o PDF' });
        return;
      }

      updateFormData({ businessDocument: file });
      
      if (file?.type?.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setBusinessDocPreview(reader?.result);
        };
        reader?.readAsDataURL(file);
      } else {
        setBusinessDocPreview(null);
      }

      setErrors({ ...errors, businessDocument: null });
    }
  };

  const handleTaxDocUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (file?.size > 10 * 1024 * 1024) {
        setErrors({ ...errors, taxDocument: 'El archivo no debe superar 10MB' });
        return;
      }

      if (!['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']?.includes(file?.type)) {
        setErrors({ ...errors, taxDocument: 'Solo se permiten archivos JPG, PNG o PDF' });
        return;
      }

      updateFormData({ taxDocument: file });
      
      if (file?.type?.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setTaxDocPreview(reader?.result);
        };
        reader?.readAsDataURL(file);
      } else {
        setTaxDocPreview(null);
      }

      setErrors({ ...errors, taxDocument: null });
    }
  };

  const handleGenerateQR = () => {
    console.log('Generating QR code for business:', formData?.commercialName);
    updateFormData({ qrCodeGenerated: true });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.emailVerified) {
      newErrors.verification = 'Debes verificar tu correo electrónico';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleComplete = () => {
    if (validateForm()) {
      console.log('Business registration form validated, calling onSubmit...');
      onSubmit();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Verificación y Código QR</h2>
        <p className="text-muted-foreground">Verifica tu cuenta y obtén tu código QR único</p>
      </div>

      {/* Submit Error Display */}
      {submitError && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={24} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900 mb-1">Error en el Registro</h3>
              <p className="text-sm text-red-700">{submitError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Email Verification */}
      <div className="bg-gradient-to-r from-accent/10 to-orange-600/10 border-2 border-accent/20 rounded-xl p-6">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="bg-accent/20 rounded-full p-6">
              <Icon name="Mail" size={48} className="text-accent" />
            </div>
          </div>
          <div>
            <p className="text-lg font-bold text-foreground mb-2">Verificación de Correo</p>
            <p className="text-muted-foreground">
              Enviaremos un código de verificación a:
            </p>
            {isEditingEmail ? (
              <div className="mt-4 space-y-3">
                <Input
                  type="email"
                  placeholder="contacto@tunegocio.com"
                  value={tempEmail}
                  onChange={(e) => setTempEmail(e?.target?.value)}
                  error={errors?.emailChange}
                />
                <div className="flex gap-2 justify-center">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleChangeEmail}
                    iconName="Check"
                    iconPosition="left"
                  >
                    Confirmar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelEmailChange}
                    iconName="X"
                    iconPosition="left"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-lg font-bold text-accent mt-2">{formData?.contactEmail}</p>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => setIsEditingEmail(true)}
                  iconName="Edit"
                  iconPosition="left"
                  className="mt-2"
                >
                  Cambiar correo
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      {!codeSent ? (
        <Button
          variant="default"
          size="lg"
          fullWidth
          onClick={handleSendCode}
          iconName="Send"
          iconPosition="right"
          className="bg-gradient-to-r from-accent to-orange-600"
        >
          Enviar Código de Verificación
        </Button>
      ) : (
        <div className="space-y-4">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <Icon name="Info" size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">Código enviado exitosamente</p>
                <p>Revisa tu bandeja de entrada. El código es válido por 10 minutos.</p>
              </div>
            </div>

            {!formData?.emailVerified && (
              <div className="pt-4 border-t border-blue-200">
                <div className="grid grid-cols-6 gap-2 mb-4">
                  {Array.from({ length: 6 }, (_, index) => (
                    <Input
                      key={index}
                      type="text"
                      maxLength={1}
                      value={verificationCode?.[index]}
                      onChange={(e) => handleCodeChange(index, e?.target?.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      ref={(el) => (inputRefs.current[index] = el)}
                      error={error}
                      className="text-center text-2xl font-bold tracking-widest"
                    />
                  ))}
                </div>

                <Button
                  variant="default"
                  size="lg"
                  fullWidth
                  onClick={handleVerify}
                  iconName="CheckCircle"
                  iconPosition="right"
                  className="mt-4 bg-gradient-to-r from-accent to-orange-600"
                  disabled={!codeSent || isLoading}
                >
                  {isLoading ? 'Verificando...' : 'Verificar Código'}
                </Button>
              </div>
            )}
          </div>

          {!formData?.emailVerified && (
            <div className="text-center">
              {canResend ? (
                <Button
                  variant="link"
                  onClick={handleResendCode}
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Reenviar código
                </Button>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Podrás reenviar en <span className="font-bold text-accent">{formatTime(remainingTime)}</span>
                </p>
              )}
            </div>
          )}
        </div>
      )}
      {formData?.emailVerified && (
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="bg-green-200 rounded-full p-3">
              <Icon name="CheckCircle" size={32} className="text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-lg font-bold text-green-900">¡Correo Verificado!</p>
              <p className="text-sm text-green-700">Tu cuenta ha sido verificada exitosamente</p>
            </div>
          </div>
        </div>
      )}
      {/* Document Uploads */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <Icon name="FileText" size={20} className="text-accent" />
          Documentos de Verificación
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Business Registration Certificate */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-foreground">
              Certificado de Registro Mercantil <span className="text-muted-foreground text-xs">(Opcional)</span>
            </label>
            <div className="border-3 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-accent transition-colors cursor-pointer relative">
              <input
                type="file"
                accept="image/jpeg,image/png,image/jpg,application/pdf"
                onChange={handleBusinessDocUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              
              {formData?.businessDocument ? (
                <div className="space-y-3">
                  {businessDocPreview && (
                    <img
                      src={businessDocPreview}
                      alt="Certificado de registro"
                      className="max-h-32 mx-auto rounded-lg shadow-md"
                    />
                  )}
                  <div className="flex items-center justify-center gap-2 text-green-600">
                    <Icon name="CheckCircle" size={20} />
                    <span className="font-semibold text-sm">{formData?.businessDocument?.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Haz clic para cambiar</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-center">
                    <div className="bg-accent/10 rounded-full p-3">
                      <Icon name="Upload" size={24} className="text-accent" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">Subir Certificado</p>
                    <p className="text-xs text-muted-foreground mt-1">JPG, PNG o PDF (máx. 10MB)</p>
                  </div>
                </div>
              )}
            </div>
            {errors?.businessDocument && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={14} />
                {errors?.businessDocument}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Puedes cargar este documento más tarde desde tu panel de control
            </p>
          </div>

          {/* Tax Document (RUT) */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-foreground">
              RUT (Registro Único Tributario) <span className="text-muted-foreground text-xs">(Opcional)</span>
            </label>
            <div className="border-3 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-accent transition-colors cursor-pointer relative">
              <input
                type="file"
                accept="image/jpeg,image/png,image/jpg,application/pdf"
                onChange={handleTaxDocUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              
              {formData?.taxDocument ? (
                <div className="space-y-3">
                  {taxDocPreview && (
                    <img
                      src={taxDocPreview}
                      alt="RUT"
                      className="max-h-32 mx-auto rounded-lg shadow-md"
                    />
                  )}
                  <div className="flex items-center justify-center gap-2 text-green-600">
                    <Icon name="CheckCircle" size={20} />
                    <span className="font-semibold text-sm">{formData?.taxDocument?.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Haz clic para cambiar</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-center">
                    <div className="bg-accent/10 rounded-full p-3">
                      <Icon name="Upload" size={24} className="text-accent" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">Subir RUT</p>
                    <p className="text-xs text-muted-foreground mt-1">JPG, PNG o PDF (máx. 10MB)</p>
                  </div>
                </div>
              )}
            </div>
            {errors?.taxDocument && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={14} />
                {errors?.taxDocument}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Puedes cargar este documento más tarde desde tu panel de control
            </p>
          </div>
        </div>
      </div>
      {/* QR Code Generation */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <Icon name="QrCode" size={20} className="text-accent" />
          Código QR del Negocio
        </h3>

        {!formData?.qrCodeGenerated ? (
          <div className="bg-gradient-to-r from-accent/10 to-orange-600/10 border-2 border-accent/20 rounded-xl p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-accent/20 rounded-full p-4">
                <Icon name="QrCode" size={48} className="text-accent" />
              </div>
            </div>
            <p className="text-foreground font-semibold mb-2">
              Genera tu código QR único
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Los clientes escanearán este código para acceder a tus descuentos exclusivos
            </p>
            <Button
              variant="default"
              size="lg"
              onClick={handleGenerateQR}
              iconName="Sparkles"
              iconPosition="right"
              className="bg-gradient-to-r from-accent to-orange-600"
              disabled={!formData?.emailVerified}
            >
              Generar Código QR
            </Button>
          </div>
        ) : (
          <div className="bg-white border-2 border-accent rounded-xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col items-center">
                <div className="w-64 h-64 bg-white border-4 border-accent rounded-xl flex items-center justify-center shadow-lg">
                  <div className="text-center">
                    <Icon name="QrCode" size={200} className="text-accent" />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-3">Vista previa del código QR</p>
              </div>

              <div className="flex flex-col justify-center space-y-4">
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Icon name="CheckCircle" size={24} className="text-green-600" />
                    <div>
                      <p className="font-bold text-green-900">¡QR Generado Exitosamente!</p>
                      <p className="text-sm text-green-700">Tu código QR está listo para usar</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Button
                    variant="outline"
                    size="lg"
                    fullWidth
                    iconName="Download"
                    iconPosition="left"
                  >
                    Descargar QR en Alta Resolución
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    fullWidth
                    iconName="Printer"
                    iconPosition="left"
                  >
                    Imprimir Material Promocional
                  </Button>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-900">
                    <strong>Código único:</strong> BN-{formData?.nit?.split('-')?.[0]}-2024
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-yellow-900">
            <p className="font-semibold mb-1">Acceso Inmediato</p>
            <p>Una vez completado el registro, podrás acceder directamente a tu panel de control empresarial. Los documentos opcionales pueden ser cargados posteriormente desde tu perfil.</p>
          </div>
        </div>
      </div>
      {Object.keys(errors)?.length > 0 && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-900">
              <p className="font-semibold mb-1">Completa todos los pasos requeridos:</p>
              <ul className="list-disc list-inside space-y-1">
                {errors?.verification && <li>{errors?.verification}</li>}
              </ul>
            </div>
          </div>
        </div>
      )}
      <div className="flex gap-4 pt-6 border-t">
        <Button
          variant="outline"
          size="lg"
          onClick={onPrevious}
          iconName="ArrowLeft"
          iconPosition="left"
          className="flex-1"
        >
          Regresar
        </Button>

        <Button
          variant="default"
          size="lg"
          onClick={handleComplete}
          disabled={isSubmitting}
          iconName="CheckCircle"
          iconPosition="right"
          className="flex-1 bg-gradient-to-r from-accent to-orange-600 hover:shadow-lg transform hover:scale-105 transition-all"
        >
          {isSubmitting ? 'Finalizando Registro...' : 'Finalizar Registro'}
        </Button>
      </div>
    </div>
  );
};

export default VerificationAndQRStep;